import { format as formatJalali } from 'date-fns-jalali';

/**
 * Persian presentation helpers.
 *
 * Two rules from `08-Non-Functional.md` section 3 drive everything here: Persian digits in
 * display, Latin digits accepted on input; and the Jalali calendar as the display default
 * with ISO-8601 storage.
 */

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'] as const;

/**
 * Rewrites Latin digits as Persian ones.
 *
 * Applied at display time only. Storing or comparing Persian digits would make every sort,
 * every equality check and every number parse subtly wrong.
 */
export function toPersianDigits(value: string | number): string {
  return String(value).replace(/\d/g, (digit) => PERSIAN_DIGITS[Number(digit)] ?? digit);
}

/**
 * Rewrites Persian and Arabic-Indic digits as Latin ones.
 *
 * Needed on the way in, because a Persian keyboard produces Persian digits and
 * `Number('۱۲')` is `NaN`. Arabic-Indic is included because some keyboards emit those
 * instead, and the two look nearly identical to a reader.
 */
export function toLatinDigits(value: string): string {
  return value
    .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x0660));
}

/**
 * Formats a civil date in the Jalali calendar.
 *
 * @param isoDate A date-only string, `YYYY-MM-DD`.
 *
 * @remarks
 * The string is split and passed to a local `Date` constructor rather than to `new
 * Date(isoDate)`. That is not fussiness: `new Date('2026-09-09')` parses as **UTC
 * midnight**, so anywhere east of Greenwich - including Tehran - it renders as the previous
 * day. A schedule date shifting by one is the exact failure
 * `frontend-standards.md` section 4 warns about.
 */
export function formatJalaliDate(isoDate: string | null | undefined, pattern = 'yyyy/MM/dd'): string {
  if (!isoDate) {
    return '—';
  }

  const [year, month, day] = isoDate.split('-').map(Number);

  if (!year || !month || !day) {
    return '—';
  }

  return toPersianDigits(formatJalali(new Date(year, month - 1, day), pattern));
}

/**
 * Formats an instant as a Jalali date, in the viewer's local time.
 *
 * @remarks
 * Separate from {@link formatJalaliDate}, which takes a date-only `YYYY-MM-DD` string and
 * splits it on the hyphen. Handed a full timestamp, that split produces `01T08:00:00Z` as the
 * day, which is not a number - so the function reports no date at all. That is what emptied
 * the created-date column on the organization-wide task list: every row showed a dash.
 *
 * Two functions rather than one that accepts both, because the difference is a decision and
 * not a parsing detail. A civil date must never be converted - a deadline is a day, and
 * converting it moves it - while an instant must be, or it is shown in the wrong day's
 * timezone. A single lenient function would silently pick one of those for the caller.
 */
export function formatJalaliInstantDate(
  iso: string | null | undefined,
  pattern = 'yyyy/MM/dd',
): string {
  if (!iso) {
    return '—';
  }

  const at = new Date(iso);

  // An unparseable timestamp reads as no date rather than as "Invalid Date" across the cell.
  return Number.isNaN(at.getTime()) ? '—' : toPersianDigits(formatJalali(at, pattern));
}

/**
 * Formats an instant in the Jalali calendar, in the viewer's local time.
 *
 * Unlike a civil date, an instant genuinely has a timezone, so it is converted rather than
 * pinned.
 */
export function formatJalaliDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }

  return toPersianDigits(formatJalali(new Date(iso), 'yyyy/MM/dd HH:mm'));
}

/**
 * A short relative description: «۳ روز پیش».
 *
 * Used where the exact instant is noise - "last synced", "updated" - and precision would
 * cost more attention than it returns.
 */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }

  const deltaMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(deltaMs / 60_000);

  if (minutes < 1) {
    return 'همین حالا';
  }

  if (minutes < 60) {
    return `${toPersianDigits(minutes)} دقیقه پیش`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `${toPersianDigits(hours)} ساعت پیش`;
  }

  const days = Math.floor(hours / 24);

  if (days < 30) {
    return `${toPersianDigits(days)} روز پیش`;
  }

  return formatJalaliDate(toIsoDate(new Date(iso)));
}

/**
 * Formats effort in hours.
 *
 * @remarks
 * `null` means nobody estimated, and it is shown as a dash rather than as zero. This
 * product tracks hours and never money, so no value here is ever formatted as currency
 * (`frontend-standards.md` section 4).
 */
export function formatHours(hours: number | null | undefined): string {
  if (hours === null || hours === undefined) {
    return '—';
  }

  const rounded = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);

  return `${toPersianDigits(rounded)} ساعت`;
}

/** Formats a count for display, in Persian digits. */
export function formatCount(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : toPersianDigits(value);
}

/**
 * Turns a picked `Date` into the `YYYY-MM-DD` the API stores.
 *
 * @remarks
 * Built from the local calendar fields rather than through `toISOString()`, and that is the
 * whole point of the function existing. `toISOString()` converts to UTC first, so a date
 * picked in Tehran comes back as the previous day for any time before 03:30 - which is every
 * date picked from a control that defaults to local midnight.
 *
 * The output is Gregorian regardless of what the picker displayed. The Jalali calendar is a
 * presentation choice; the wire format is ISO-8601, as `08-Non-Functional.md` section 3
 * requires.
 */
export function toIsoDate(value: Date | null | undefined): string | null {
  if (!value || Number.isNaN(value.getTime())) {
    return null;
  }

  const year = String(value.getFullYear()).padStart(4, '0');
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Turns a stored `YYYY-MM-DD` back into a `Date` a picker can hold.
 *
 * @remarks
 * The mirror image of {@link toIsoDate}, and local for the same reason: `new
 * Date('2026-09-09')` parses as UTC midnight and renders as the previous day anywhere east
 * of Greenwich.
 */
export function fromIsoDate(isoDate: string | null | undefined): Date | null {
  if (!isoDate) {
    return null;
  }

  const [year, month, day] = isoDate.split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
}

/**
 * Days until a date, negative when overdue.
 *
 * Compared at local midnight on both sides, so "today" means the whole day rather than the
 * instant the page happened to load.
 */
export function daysUntil(isoDate: string | null | undefined): number | null {
  if (!isoDate) {
    return null;
  }

  const [year, month, day] = isoDate.split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }

  const target = new Date(year, month - 1, day);
  const today = new Date();

  target.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}
